import { motion, MotionProps } from "motion/react";
import React, { ComponentPropsWithoutRef, forwardRef, ReactNode } from "react";
import "./button.css";
import { LoaderIcon } from "../../../constants/icons";
import {
  CSSColorVariantType,
  UserButtonActionsType,
} from "../../../types/common";
import { useVibration } from "../../../hooks/useVibration";
import { useWidth } from "../../../hooks/useMobile";

// type
export type ButtonProps = {
  variant?: CSSColorVariantType;
  action?: UserButtonActionsType;
  shape?: "circle" | "oval" | "";
  className?: string;
  Icon?: React.ReactNode;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  textTransform?: "capitalize" | "lowercase" | "uppercase" | "";
  fullWidth?: boolean;
  center?: boolean;
  isHoverEffect?: boolean;
  size?: "extra-small" | "small" | "regular" | "medium" | "large";
  isLoading?: boolean;
  loadingText?: string;
} & MotionProps &
  ComponentPropsWithoutRef<"button">;

// Custom Button Component
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      action = "primary",
      shape = "",
      className = "",
      Icon,
      textTransform = "",
      fullWidth = false,
      center = false,
      isHoverEffect = false,
      startIcon,
      endIcon,
      size = "medium",
      isLoading = false,
      loadingText,
      disabled,
      ...props
    },
    ref
  ) => {
    const finalStartIcon = Icon || startIcon;
    const restStyle = typeof props.style === "object" ? props.style : {};

    const { vibrate } = useVibration();
    const { mobileView } = useWidth();


    return (
      <motion.button
        variants={{
          rest: {
            scale: 1,
            boxShadow: "0 1px 1px rgba(0, 0, 0, 0.5)",
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 25,
              mass: 0.5,
              duration: 0.1,
            },
          },
          hover: {
            scale: 1.02,
            boxShadow: "none",
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 25,
              mass: 0.5,
              duration: 0.1,
            },
          },
          tap: {
            scale: 0.97,
            boxShadow: `0 0 0 0.3rem color-mix(in srgb, currentColor 100%, black 50%)`,
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 25,
              mass: 0.5,
              duration: 0.1,
            },
          },
          active: {
            scale: 0.95,
            boxShadow: "none",
            transition: {
              type: "spring",
              stiffness: 400,
              damping: 25,
              mass: 0.5,
              duration: 0.1,
            },
          },
        }}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        animate="rest"
        data-touch-target="true"
        ref={ref}
        style={{
          touchAction: "manipulation",
          WebkitTapHighlightColor: "transparent",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          userSelect: "none",
          cursor: "pointer",
          willChange: "transform",
          ...restStyle,
        }}
        aria-busy={isLoading || disabled}
        aria-disabled={disabled}
        className={`taptap-btn px-1 py-8 br-4 fw-500 border-none outline-none user-select-none fs-button pointer size-${size} ${
          action !== "tertiary" ? "button" : ""
        } ${textTransform} ${
          finalStartIcon || endIcon ? "flex ai-center gap-4" : ""
        } ${shape} button-${variant}-${action} ${className} ${
          fullWidth ? "w-100" : "max-content"
        } ${center ? "mx-auto" : ""} 
        ${isHoverEffect ? "hover-effect-on" : "hover-effect-off"}
        `}
        disabled={isLoading || disabled}
        onTouchStart={(e) => {
          if(mobileView) vibrate(50);
          props.onTouchStart?.(e);
        }}
        onClick={(e) => {
          props.onClick?.(e);
        }}
        {...props}
      >
        {isLoading && <LoaderIcon className="loading-spinner-animation" />}
        {finalStartIcon && !isLoading && finalStartIcon}
        {loadingText && isLoading ? loadingText : children}
        {endIcon && !isLoading && endIcon}
      </motion.button>
    );
  }
);

export default Button;
